import * as React from 'react';
import { Model } from 'survey-core';
import { FormControlLabel, Radio, RadioGroup, styled, Typography, List, ListItem, Box } from '@material-ui/core';
import { ItemValue } from 'survey-core';
import { MuiSubscriptions, MuiSubscriptionsModel } from './MuiSubscriptions';
import { SupportServices } from '../../types/enums';

export const TYPE = 'mui-additional-products';
export const SELF_SERVE_VALUE = 'selfServe';
const additionalProductSortingOrder = [
  SupportServices.SelfServe,
  SupportServices.FastTrack,
  SupportServices.CHASAssist
];

export class MuiAdditionalProductsModel extends MuiSubscriptionsModel {
  getType(): string {
    return TYPE;
  }

  get selfServeText() {
    return this.getPropertyValue('selfServeText');
  }
  set selfServeText(value) {
    this.setPropertyValue('selfServeText', value);
  }
}

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  gap: '10%',
  margin: theme.spacing(2, 0),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    margin: theme.spacing(1.5, 0, 0)
  },
  '& > div': {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2.25)
    }
  },
  '& .MuiFormControlLabel-root': {
    alignItems: 'baseline',
    '& .MuiTypography-caption': {
      color: 'var(--light-outline)'
    }
  },
  '& .MuiTypography-root.MuiTypography-body1, & .MuiTypography-root.MuiTypography-body2': {
    transform: 'translateY(-8px)'
  },
  '& .MuiList-root': {
    listStyleType: 'disc',
    paddingLeft: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(-0.5),
      paddingLeft: theme.spacing(3)
    },
    '& .MuiListItem-root': {
      padding: 0,
      display: 'list-item',
      [theme.breakpoints.down('md')]: {
        marginBottom: theme.spacing(-0.25)
      },
      '&::marker': {
        fontSize: 10
      }
    }
  }
}));
const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  flexShrink: 0,
  margin: 0,
  textAlign: 'center',
  boxSizing: 'border-box',
  '& .MuiRadio-root': {
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(-1),
      marginRight: theme.spacing(0.5)
    }
  },
  '& .MuiTypography-caption': {
    [theme.breakpoints.down('md')]: {
      display: 'block',
      marginBottom: theme.spacing(-0.25)
    }
  }
}));

export class MuiAdditionalProducts extends MuiSubscriptions {
  get selfServeText() {
    return this.question.selfServeText;
  }

  getChoicesForRender(choices: ItemValue[]) {
    const { survey } = this.question;
    const parentQuestion = survey.getQuestionByName(this.question.choicesFromQuestion) as MuiSubscriptionsModel;
    const filteredChoices = super.getChoicesForRender(choices).filter(item => {
      if (parentQuestion.selectedItem && item.originalItem && parentQuestion.selectedItem.originalItem) {
        return item.originalItem.subscriptionTier === parentQuestion.selectedItem.originalItem.subscriptionTier;
      }
      return false;
    });

    this.updateFilteredProducts(filteredChoices);

    return filteredChoices.sort(function(a, b) {
      return (
        additionalProductSortingOrder.indexOf(a.originalItem.purchaseType) -
        additionalProductSortingOrder.indexOf(b.originalItem.purchaseType)
      );
    });
  }

  protected renderElement(): JSX.Element | null {
    const { value = '', survey } = this.question;
    const parentQuestion = survey.getQuestionByName(this.question.choicesFromQuestion) as MuiSubscriptionsModel;
    const choices = this.getChoicesForRender(parentQuestion.visibleChoices);
    const taxRate = (survey as Model).getVariable('taxRate');

    return (
      <StyledRadioGroup row value={value} onChange={this.handleChange}>
        <Box flex={1} mb={1}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <StyledFormControlLabel
              control={<Radio color="primary" inputProps={{ tabIndex: 1 }} />}
              label={<Typography variant="h6">{this.selfServeText}</Typography>}
              labelPlacement="end"
              value={SELF_SERVE_VALUE}
            />
          </Box>
          <List disablePadding>
            {super.getString('selfServeAdditionalProducts').map((text: string, index: number) => (
              <ListItem key={index}>
                <Typography variant="caption">{text}</Typography>
              </ListItem>
            ))}
          </List>
        </Box>
        {choices.map(({ value, originalItem }: ItemValue) => (
          <Box flex={1} key={value}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="baseLine" mb={0.5}>
                <StyledFormControlLabel
                  key={value}
                  control={<Radio color="primary" inputProps={{ tabIndex: 1 }} />}
                  label={
                    <>
                      <Typography variant="h6">{originalItem.purchaseType}</Typography>
                      {originalItem.purchaseType === SupportServices.CHASAssist && (
                        <Typography variant="caption">{super.getString('includesFastTrack')}</Typography>
                      )}
                    </>
                  }
                  labelPlacement="end"
                  value={value}
                />
                <Typography variant="body2">{`+ ${super.getPrice(value)}${taxRate?.isActive ? '*' : ''}`}</Typography>
              </Box>
            </Box>
            <List disablePadding>
              {super.getProductDetails(originalItem.purchaseType)?.description.map((description, index) => (
                <ListItem key={index}>
                  <Typography variant="caption">{description.text}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </StyledRadioGroup>
    );
  }
}
